import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Ban,
  CheckCircle2,
  ClipboardList,
  Eye,
  BarChart3,
  Loader2,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  RefreshCw,
  Users,
  type LucideIcon,
} from 'lucide-react';
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { api } from '@/lib/api/client';
import { ensureArray } from '@/lib/utils/ensure-array';
import { CampaignStatusBadge } from '@/features/campaigns/components/CampaignStatusBadge';
import {
  templateLabel,
  type CampaignRow,
} from '@/features/campaigns/types';
import { useDashboardMain } from '../api';

/* ---------------------------------------------------------------- *
 * Shared helpers
 * ---------------------------------------------------------------- */

const fmt = (n: number | undefined | null): string =>
  typeof n === 'number' && !Number.isNaN(n) ? n.toLocaleString() : '0';

const pct = (n: number, total: number): string => {
  if (!total) return '0%';
  return `${((n / total) * 100).toFixed(1)}%`;
};

interface MetricTileProps {
  label: string;
  value: number;
  icon: LucideIcon;
  tone: 'slate' | 'green' | 'emerald' | 'purple' | 'red' | 'orange' | 'sky';
  /** "Sub-stat" shown under the count (e.g. "vs yesterday" delta or %). */
  hint?: { value: string; tone?: 'up' | 'down' | 'neutral' };
  loading?: boolean;
}

const TONE_BG: Record<MetricTileProps['tone'], string> = {
  slate: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  green: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
  emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
  purple: 'bg-violet-100 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400',
  red: 'bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-950/40 dark:text-orange-400',
  sky: 'bg-sky-100 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400',
};

/**
 * The KPI tile used across the top KPI strip + the bottom Rate strip.
 * Each tile is its own widget so users can re-order / hide them via the
 * existing customize-mode UX.
 */
function MetricTile({ label, value, icon: Icon, tone, hint, loading }: MetricTileProps) {
  /*
   * Compact body so 6 tiles fit per row at lg without count truncation.
   * Smaller icon (h-9), smaller count (text-lg), label + delta on top.
   * The numbers can grow to 8-9 digits ("20,821,184") — text-lg with
   * tabular-nums keeps them readable in ~190px content width.
   */
  return (
    <div className="flex h-full items-center gap-2">
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          TONE_BG[tone],
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <span className="truncate text-[10px] text-muted-foreground">{label}</span>
          {hint && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 rounded px-1 py-0 text-[9px] font-medium tabular-nums',
                hint.tone === 'up' &&
                  'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
                hint.tone === 'down' &&
                  'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
                (hint.tone === 'neutral' || !hint.tone) &&
                  'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
              )}
            >
              {hint.tone === 'up' && '▲ '}
              {hint.tone === 'down' && '▼ '}
              {hint.value}
            </span>
          )}
        </div>
        {loading ? (
          <Skeleton className="mt-1 h-5 w-20" />
        ) : (
          <div className="truncate text-lg font-semibold tabular-nums leading-tight text-foreground">
            {value.toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Header KPI tiles (Total Campaigns, Dialed, Connected, Pending,
 * DND Numbers, Retry Count). Each one is its own widget.
 * ---------------------------------------------------------------- */

/**
 * Aggregate campaign rows across the user's FULL list. Each role uses the
 * endpoint THEY have access to — the same endpoints the Campaign Summary
 * pages (Live / Historical / Scheduled) use, which we know return data:
 *   USER  → GET /api/obd/campaign/{userId}             (useLiveCampaigns)
 *   ADMIN → GET /api/obd/reseller/campaign/list/{id}   (useAdminLiveCampaigns)
 *   SADMIN/SUB → GET /api/obd/superadmin/campaign/list/{id}
 *                                                     (useSuperAdminCampaigns)
 *
 * The S_ADMIN endpoint is not authorized for regular users, so calling it
 * universally returned empty for everyone except super-admins. Routing per
 * role mirrors the legacy app's actual behavior.
 */
type AggRow = Partial<CampaignRow> & {
  templateId?: number | string;
  campaignStatus?: number | string;
  campaignName?: string;
};

/**
 * Single `useQuery` call whose URL is chosen by role. Critical: only ONE
 * request fires per render — calling per-role hooks unconditionally fires
 * every variant, and the super-admin URL returns 403 for non-superadmin
 * users, polluting the network log with Forbidden errors.
 */
function useCampaignAggregateSource(): {
  rows: AggRow[];
  isLoading: boolean;
} {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const isSadmin =
    role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;
  const isAdmin = role === USER_ROLES.ADMIN;

  const url = isSadmin
    ? `/api/obd/superadmin/campaign/list`
    : isAdmin
    ? `/api/obd/reseller/campaign/list/${userId}`
    : `/api/obd/campaign/${userId}`;

  const q = useQuery({
    queryKey: ['dashboard', 'campaign-aggregate', role, userId],
    queryFn: () => api.get<AggRow[]>(url),
    enabled: Boolean(userId && role),
    staleTime: 30_000,
    select: (d) => ensureArray<AggRow>(d),
  });
  return { rows: q.data ?? [], isLoading: q.isLoading };
}

function useDashboardTotals() {
  const { rows, isLoading } = useCampaignAggregateSource();
  return useMemo(() => {
    let dialed = 0;
    let connected = 0;
    let dnd = 0;
    let retry = 0;
    let uploaded = 0;
    let totalReady = 0;
    for (const c of rows) {
      const up = Number(c.numbersUploaded ?? 0);
      uploaded += up;
      dialed += Number(c.numbersProcessed ?? 0);
      connected += Number(c.callsConnected ?? 0);
      dnd += Number(c.dndCount ?? 0);
      retry += Number(c.retryCount ?? 0);
      if (Number(c.campaignStatus) === 0) totalReady += up;
    }
    const pending = Math.max(0, uploaded - dnd - dialed);
    return {
      isLoading,
      totalCampaigns: rows.length,
      dialed,
      connected,
      pending,
      dnd,
      retry,
      uploaded,
      totalReady,
      rows,
    };
  }, [rows, isLoading]);
}

export function TotalCampaignsKpi() {
  const t = useDashboardTotals();
  return (
    <MetricTile
      label="Total Campaigns"
      value={t.totalCampaigns}
      icon={ClipboardList}
      tone="slate"
      loading={t.isLoading}
    />
  );
}

export function DialedCallsKpi() {
  const t = useDashboardTotals();
  return (
    <MetricTile
      label="Dialed Calls"
      value={t.dialed}
      icon={PhoneOutgoing}
      tone="green"
      hint={{ value: pct(t.dialed, t.uploaded || t.dialed), tone: 'up' }}
      loading={t.isLoading}
    />
  );
}

export function ConnectedCallsKpi() {
  const t = useDashboardTotals();
  return (
    <MetricTile
      label="Connected Calls"
      value={t.connected}
      icon={CheckCircle2}
      tone="emerald"
      hint={{ value: pct(t.connected, t.dialed || t.uploaded), tone: 'up' }}
      loading={t.isLoading}
    />
  );
}

export function PendingCallsKpi() {
  const t = useDashboardTotals();
  return (
    <MetricTile
      label="Pending Calls"
      value={t.pending}
      icon={Loader2}
      tone="purple"
      hint={{ value: pct(t.pending, t.uploaded), tone: 'down' }}
      loading={t.isLoading}
    />
  );
}

export function DndNumbersKpi() {
  const t = useDashboardTotals();
  return (
    <MetricTile
      label="DND Numbers"
      value={t.dnd}
      icon={Ban}
      tone="red"
      hint={{ value: pct(t.dnd, t.uploaded), tone: 'down' }}
      loading={t.isLoading}
    />
  );
}

export function RetryCountKpi() {
  const t = useDashboardTotals();
  return (
    <MetricTile
      label="Retry Count"
      value={t.retry}
      icon={RefreshCw}
      tone="orange"
      hint={{ value: pct(t.retry, t.uploaded), tone: 'down' }}
      loading={t.isLoading}
    />
  );
}

/* ---------------------------------------------------------------- *
 * Daily Call Traffic — line chart with day-range toggle (7 / 14 / 30)
 * ---------------------------------------------------------------- */

export function DailyCallTrafficWidget() {
  const dash = useDashboardMain();
  const [range, setRange] = useState<'7' | '14' | '30'>('7');

  /*
   * The dashboard payload exposes per-day expenditure rows. We re-use the
   * date axis but plot the campaign count (`dbWeeklyCampaigns`) joined by
   * date so the trend reflects call volume rather than rupees. When the
   * series is shorter than the selected range we just render what we
   * have — the chart still reads correctly.
   */
  const data = useMemo(() => {
    const expRows = dash.data?.dbExpenditure ?? [];
    const wkRows = dash.data?.dbWeeklyCampaigns ?? [];
    const byDate = new Map<string, { date: string; calls: number }>();
    for (const r of expRows) {
      byDate.set(r.campaignDate, { date: r.campaignDate, calls: 0 });
    }
    for (const r of wkRows) {
      const slot = byDate.get(r.scheduleDate) ?? { date: r.scheduleDate, calls: 0 };
      slot.calls = Number(r.campaignCount ?? 0);
      byDate.set(r.scheduleDate, slot);
    }
    const arr = Array.from(byDate.values()).sort((a, b) =>
      a.date < b.date ? -1 : 1,
    );
    return arr.slice(-Number(range));
  }, [dash.data, range]);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">Daily Call Traffic</span>
        <Select value={range} onValueChange={(v) => setRange(v as typeof range)}>
          <SelectTrigger className="h-7 w-[120px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 Days</SelectItem>
            <SelectItem value="14">Last 14 Days</SelectItem>
            <SelectItem value="30">Last 30 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1">
        {dash.isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : data.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            No traffic data yet.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="date"
                fontSize={10}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis
                fontSize={10}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="calls"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 3, fill: '#3b82f6' }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Donut: Call Outcome Summary
 * ---------------------------------------------------------------- */

const OUTCOME_COLORS = {
  Connected: '#10b981',
  Pending: '#f59e0b',
  DND: '#3b82f6',
  Failed: '#f43f5e',
} as const;

export function CallOutcomeDonutWidget() {
  const t = useDashboardTotals();
  const failed = Math.max(
    0,
    t.dialed - t.connected - t.dnd > 0 ? t.dialed - t.connected - t.dnd : 0,
  );
  const data = [
    { name: 'Connected', value: t.connected },
    { name: 'Pending', value: t.pending },
    { name: 'DND', value: t.dnd },
    { name: 'Failed', value: failed },
  ];
  const totalForPct = data.reduce((a, b) => a + b.value, 0);

  if (t.isLoading) return <Skeleton className="h-full w-full" />;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-semibold text-foreground">Call Outcome Summary</div>
      <div className="grid flex-1 grid-cols-2 items-center gap-3">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="85%"
              paddingAngle={2}
              stroke="none"
            >
              {data.map((d) => (
                <Cell key={d.name} fill={OUTCOME_COLORS[d.name as keyof typeof OUTCOME_COLORS]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 6,
                fontSize: 12,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <ul className="space-y-1.5 text-xs">
          {data.map((d) => (
            <li key={d.name} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ background: OUTCOME_COLORS[d.name as keyof typeof OUTCOME_COLORS] }}
                />
                <span className="text-foreground">{d.name}</span>
              </span>
              <span className="tabular-nums text-muted-foreground">
                {pct(d.value, totalForPct)} ({fmt(d.value)})
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Donut: Campaign Type Overview
 * ---------------------------------------------------------------- */

const TYPE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#f43f5e', '#06b6d4', '#6366f1'];

export function CampaignTypeDonutWidget() {
  const { rows, isLoading } = useCampaignAggregateSource();
  const data = useMemo(() => {
    const tpl = new Map<number, number>();
    for (const c of rows) {
      const k = Number(c.templateId ?? -1);
      tpl.set(k, (tpl.get(k) ?? 0) + 1);
    }
    return Array.from(tpl.entries())
      .map(([k, v]) => ({ name: templateLabel(k), value: v }))
      .sort((a, b) => b.value - a.value);
  }, [rows]);
  const total = data.reduce((a, b) => a + b.value, 0);

  if (isLoading) return <Skeleton className="h-full w-full" />;
  if (data.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <div className="mb-2 text-sm font-semibold text-foreground">Campaign Type Overview</div>
        <p className="flex flex-1 items-center justify-center py-6 text-center text-xs text-muted-foreground">
          No campaign type data.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-semibold text-foreground">Campaign Type Overview</div>
      <div className="grid flex-1 grid-cols-2 items-center gap-3">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="85%"
              paddingAngle={2}
              stroke="none"
            >
              {data.map((_, i) => (
                <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 6,
                fontSize: 12,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <ul className="space-y-1.5 text-xs">
          {data.map((d, i) => (
            <li key={d.name} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ background: TYPE_COLORS[i % TYPE_COLORS.length] }}
                />
                <span className="text-foreground">{d.name}</span>
              </span>
              <span className="tabular-nums text-muted-foreground">{pct(d.value, total)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Top 10 Campaigns by Dialed Calls
 * ---------------------------------------------------------------- */

export function TopCampaignsByDialedWidget() {
  const { rows: allRows, isLoading } = useCampaignAggregateSource();
  const rows = useMemo(
    () =>
      [...allRows]
        .sort(
          (a, b) =>
            Number(b.numbersProcessed ?? 0) - Number(a.numbersProcessed ?? 0),
        )
        .slice(0, 10),
    [allRows],
  );
  const max = rows[0] ? Number(rows[0].numbersProcessed ?? 0) : 0;

  if (isLoading) return <Skeleton className="h-full w-full" />;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">
          Top 10 Campaigns (By Dialed Calls)
        </span>
        <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
          <Link to="/voice-campaign-summary">View All</Link>
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-xs text-muted-foreground">No campaigns yet.</p>
      ) : (
        <ul className="flex-1 space-y-2.5 overflow-y-auto">
          {rows.map((r) => {
            const dialed = Number(r.numbersProcessed ?? 0);
            const w = max ? (dialed / max) * 100 : 0;
            return (
              <li key={r.campaignId} className="grid grid-cols-[1fr_auto] items-center gap-3 text-xs">
                <div className="min-w-0">
                  <Link
                    to={`/voice-campaign-info/${r.campaignId}`}
                    className="block truncate font-medium text-primary hover:underline"
                  >
                    {r.campaignName}
                  </Link>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-sky-500"
                      style={{ width: `${w}%` }}
                    />
                  </div>
                </div>
                <span className="tabular-nums text-muted-foreground">{fmt(dialed)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Recent Campaigns — compact 5-row table
 * ---------------------------------------------------------------- */

export function RecentCampaignsWidget() {
  const { rows: allRows, isLoading } = useCampaignAggregateSource();
  const rows = allRows.slice(0, 5);

  if (isLoading) return <Skeleton className="h-full w-full" />;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">Recent Campaigns</span>
        <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
          <Link to="/voice-campaign-summary">View All</Link>
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-xs text-muted-foreground">No campaigns yet.</p>
      ) : (
        <div className="flex-1 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="pb-2 text-left font-medium">Campaign</th>
                <th className="pb-2 text-left font-medium">Status</th>
                <th className="pb-2 text-right font-medium">Dialed</th>
                <th className="pb-2 text-right font-medium">Connected</th>
                <th className="pb-2 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.campaignId} className="hover:bg-muted/40">
                  <td className="py-1.5">
                    <Link
                      to={`/voice-campaign-info/${r.campaignId}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {r.campaignName}
                    </Link>
                  </td>
                  <td className="py-1.5">
                    <CampaignStatusBadge status={r.campaignStatus} />
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {fmt(Number(r.numbersProcessed ?? 0))}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {fmt(Number(r.callsConnected ?? 0))}
                  </td>
                  <td className="py-1.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        title="View"
                      >
                        <Link to={`/voice-campaign-info/${r.campaignId}`}>
                          <Eye className="h-3 w-3" />
                        </Link>
                      </Button>
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        title="Report"
                      >
                        <Link to={`/voice-campaign-info/${r.campaignId}`}>
                          <BarChart3 className="h-3 w-3" />
                        </Link>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Rate KPIs (bottom strip in the reference design)
 * ---------------------------------------------------------------- */

export function AnswerRateKpi() {
  const t = useDashboardTotals();
  const rate = t.dialed > 0 ? (t.connected / t.dialed) * 100 : 0;
  return (
    <MetricTile
      label="Answer Rate"
      value={Math.round(rate * 10) / 10}
      icon={PhoneIncoming}
      tone="emerald"
      loading={t.isLoading}
      hint={{ value: '%', tone: 'neutral' }}
    />
  );
}

export function DndRateKpi() {
  const t = useDashboardTotals();
  const rate = t.uploaded > 0 ? (t.dnd / t.uploaded) * 100 : 0;
  return (
    <MetricTile
      label="DND Rate"
      value={Math.round(rate * 10) / 10}
      icon={Ban}
      tone="red"
      loading={t.isLoading}
      hint={{ value: '%', tone: 'neutral' }}
    />
  );
}

export function RetryRateKpi() {
  const t = useDashboardTotals();
  const rate = t.uploaded > 0 ? (t.retry / t.uploaded) * 100 : 0;
  return (
    <MetricTile
      label="Retry Rate"
      value={Math.round(rate * 10) / 10}
      icon={RefreshCw}
      tone="orange"
      loading={t.isLoading}
      hint={{ value: '%', tone: 'neutral' }}
    />
  );
}

export function FailedCallsKpi() {
  const t = useDashboardTotals();
  const failed = Math.max(0, t.dialed - t.connected - t.dnd);
  return (
    <MetricTile
      label="Failed Calls"
      value={failed}
      icon={PhoneMissed}
      tone="red"
      loading={t.isLoading}
      hint={{ value: pct(failed, t.dialed), tone: 'down' }}
    />
  );
}

export function TotalUsersKpi() {
  const dash = useDashboardMain();
  const total =
    Number(dash.data?.dbHeader?.endUsers ?? 0) +
    Number(dash.data?.dbHeader?.resellers ?? 0);
  return (
    <MetricTile
      label="Total Users"
      value={total}
      icon={Users}
      tone="sky"
      loading={dash.isLoading}
    />
  );
}

/* ---------------------------------------------------------------- *
 * Additional KPIs available from the legacy APIs but not in the
 * reference screenshot. All matched to fields the legacy dashboard
 * already reads from the same endpoints, so the numbers line up.
 * ---------------------------------------------------------------- */

export function TotalNumbersKpi() {
  const t = useDashboardTotals();
  return (
    <MetricTile
      label="Total Numbers"
      value={t.uploaded}
      icon={ClipboardList}
      tone="sky"
      loading={t.isLoading}
    />
  );
}

export function TotalCallsDialedKpi() {
  const t = useDashboardTotals();
  const tcd = t.dialed + t.retry;
  return (
    <MetricTile
      label="Total Calls Dialed"
      value={tcd}
      icon={PhoneOutgoing}
      tone="orange"
      hint={{ value: pct(tcd, t.uploaded), tone: 'neutral' }}
      loading={t.isLoading}
    />
  );
}

export function TotalReadyKpi() {
  const t = useDashboardTotals();
  return (
    <MetricTile
      label="Total Ready"
      value={t.totalReady}
      icon={CheckCircle2}
      tone="sky"
      hint={{ value: pct(t.totalReady, t.uploaded), tone: 'neutral' }}
      loading={t.isLoading}
    />
  );
}
